import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Input, Loading } from '@ibk/shared-ui';
import { formatCurrency, parseCurrencyToNumber } from '@ibk/shared-utils';
import styles from './LimitsEditor.module.css';
function PendingNoticeIcon({ className }) {
    return (_jsx("svg", { className: className, viewBox: '0 0 20 20', fill: 'currentColor', children: _jsx("path", { fillRule: 'evenodd', d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z', clipRule: 'evenodd' }) }));
}
function PendingNotice({ message }) {
    return (_jsxs("div", { className: styles.pendingNotice, children: [_jsx(PendingNoticeIcon, { className: styles.pendingIcon }), message] }));
}
function ValidationMessage({ message, isError }) {
    return _jsx("div", { className: isError ? styles.validationError : styles.validationMessage, children: message });
}
function LimitInput({ dataTestId, label, description, value, onChange, onFocus, onBlur, disabled, isPending, pendingMessage, isFocused, minLimit, maxLimit, }) {
    const { t } = useTranslation();
    const labelWithDescription = description ? `${label} (${description})` : label;
    const numericValue = value ? parseCurrencyToNumber(value.replace('R$', '').trim()) : 0;
    const isValueZeroOrAboveMax = numericValue === 0 || numericValue > maxLimit;
    const hasError = isValueZeroOrAboveMax;
    const getValidationMessage = () => {
        if (!isFocused || !isValueZeroOrAboveMax)
            return '';
        const minFormatted = formatCurrency(minLimit);
        const maxFormatted = formatCurrency(maxLimit);
        return t('limits.validation-range', { min: minFormatted, max: maxFormatted });
    };
    const validationMessage = getValidationMessage();
    return (_jsxs("div", { className: styles.limitRow, children: [_jsx(Input, { dataTestId: dataTestId, size: 'large', label: labelWithDescription, placeholder: 'R$ 0,00', value: value, onChange: onChange, onFocus: onFocus, onBlur: onBlur, type: 'text', inputMode: 'decimal', disabled: disabled, autoComplete: 'off' }), isPending && pendingMessage && _jsx(PendingNotice, { message: pendingMessage }), isFocused && validationMessage && _jsx(ValidationMessage, { message: validationMessage, isError: hasError })] }));
}
export function LimitsEditor({ pixLimitsHook, onClose }) {
    const { t } = useTranslation();
    const { limits, pendingLimits, ruleSlugsMap, maxLimits, isLoading, isSaving, hasChanges, handleLimitChange, handleOpenPinModal, handleCancel, } = pixLimitsHook;
    const [focusedField, setFocusedField] = useState(null);
    const MIN_LIMIT = 0.01;
    const handleClose = useCallback(() => {
        handleCancel();
        onClose?.();
    }, [handleCancel, onClose]);
    const handleCurrencyInputChange = useCallback((field) => (e) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/\D/g, '');
        if (numericValue === '') {
            handleLimitChange(field, '0,00');
            return;
        }
        const number = parseInt(numericValue, 10) / 100;
        const formatted = new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(number);
        handleLimitChange(field, formatted);
    }, [handleLimitChange]);
    const getFormattedValue = (field) => {
        const value = limits[field];
        if (!value || value === '0,00')
            return '';
        if (value.includes('R$'))
            return value;
        return `R$ ${value}`;
    };
    const hasValidationErrors = useCallback(() => {
        const fields = ['personDay', 'personNight', 'businessDay', 'businessNight', 'scheduledAll'];
        return fields.some(field => {
            const numericValue = limits[field] ? parseCurrencyToNumber(limits[field]) : 0;
            return numericValue < MIN_LIMIT || numericValue > maxLimits.max;
        });
    }, [limits, maxLimits]);
    const getLimitInputProps = (field, label, description, dataTestId) => {
        const ruleSlug = ruleSlugsMap[field];
        const isPending = pendingLimits.some(pl => pl.ruleSlug === ruleSlug);
        const pending = pendingLimits.find(pl => pl.ruleSlug === ruleSlug);
        const pendingValue = pending ? formatCurrency(pending.value) : undefined;
        return {
            dataTestId,
            label,
            description,
            value: getFormattedValue(field),
            onChange: handleCurrencyInputChange(field),
            onFocus: () => setFocusedField(field),
            onBlur: () => setFocusedField(null),
            disabled: isPending,
            isPending,
            pendingMessage: isPending && pendingValue ? t('limits.pending-request', { value: pendingValue }) : undefined,
            isFocused: focusedField === field,
            minLimit: MIN_LIMIT,
            maxLimit: maxLimits.max,
        };
    };
    const handleSaveClick = () => {
        if (hasChanges && !isSaving && !hasValidationErrors()) {
            handleOpenPinModal();
        }
    };
    if (isLoading) {
        return (_jsx("div", { className: styles.loading, children: _jsx(Loading, {}) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.alert, children: [_jsx("svg", { className: styles.alertIcon, viewBox: '0 0 20 20', fill: 'currentColor', children: _jsx("path", { fillRule: 'evenodd', d: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z', clipRule: 'evenodd' }) }), _jsx("span", { className: styles.alertText, children: t('limits.alert-increase') })] }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: t('limits.section-person') }), _jsx(LimitInput, { ...getLimitInputProps('personDay', t('limits.day'), t('limits.day-description'), 'limit-person-day') }), _jsx(LimitInput, { ...getLimitInputProps('personNight', t('limits.night'), t('limits.night-description'), 'limit-person-night') })] }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: t('limits.section-business') }), _jsx(LimitInput, { ...getLimitInputProps('businessDay', t('limits.day'), t('limits.day-description'), 'limit-business-day') }), _jsx(LimitInput, { ...getLimitInputProps('businessNight', t('limits.night'), t('limits.night-description'), 'limit-business-night') })] }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: t('limits.section-scheduled') }), _jsx(LimitInput, { ...getLimitInputProps('scheduledAll', t('limits.all-day'), t('limits.all-day-description'), 'limit-scheduled-all') })] }), _jsxs("div", { className: styles.actions, children: [_jsx(Button, { variant: 'secondary', size: 'large', onClick: handleClose, disabled: isSaving, dataTestId: 'button-cancel', children: t('limits.cancel') }), _jsx(Button, { variant: 'primary', size: 'large', onClick: handleSaveClick, disabled: !hasChanges || isSaving || hasValidationErrors(), dataTestId: 'button-save', children: t('limits.save') })] })] }));
}
